import { useState } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { parseDateLocal } from "@/lib/format";
import { queryKeys } from "@/lib/queryKeys";
import { Icon } from "@/components/Icon";
import { ProjectEditModal } from "@/components/ProjectEditModal";
import { ProjectHeader } from "@/components/ProjectHeader";
import { Skeleton } from "@/components/Skeleton";

/**
 * Layout route for /projects/$projectId/*.
 *
 * T38 / T40: All project sub-tabs (Board, List, Activity, Members) share this
 * single shell. The shell owns the breadcrumb, the rich ProjectHeader (name,
 * owner, due date, status pill, progress) and the tab strip. Children render
 * inside the `.proj-shell-body` outlet — switching tabs only swaps content,
 * the header / tabs do not flash or re-mount.
 *
 * The shell is a flex column pinned to the viewport-minus-topbar so each
 * child can opt into a full-bleed layout (board, list) by simply growing to
 * `flex: 1` inside `.proj-shell-body`. Padded centered layouts (activity,
 * members) still work — they just grow inside the same body.
 *
 * The active tab is derived from the current pathname so child routes don't
 * have to pass an `active` prop.
 */
function ProjectLayout() {
  const { projectId } = Route.useParams();
  const { pathname } = useLocation();

  // Match the LAST segment to choose the active tab. Board (no sub-path) and
  // the new-task page both fall under the parent — only the leaf segments
  // /list, /activity, /members shift the highlight off "board".
  let active:
    | "board"
    | "milestones"
    | "epics"
    | "docs"
    | "wiki"
    | "requirements"
    | "reports"
    | "activity"
    | "members"
    | "files"
    | "settings" = "board";
  // /list (and /table, /calendar, /timeline, /gantt) are views of the Tasks
  // tab — they keep "Tasks" (board) highlighted, so no own tab.
  if (pathname.endsWith("/milestones")) active = "milestones";
  else if (pathname.endsWith("/epics")) active = "epics";
  else if (pathname.endsWith("/docs")) active = "docs";
  else if (pathname.endsWith("/wiki")) active = "wiki";
  else if (pathname.endsWith("/requirements")) active = "requirements";
  else if (pathname.endsWith("/reports")) active = "reports";
  else if (pathname.endsWith("/activity")) active = "activity";
  else if (pathname.endsWith("/members")) active = "members";
  else if (pathname.endsWith("/files")) active = "files";
  else if (pathname.endsWith("/settings")) active = "settings";

  const project = useQuery({
    queryKey: queryKeys.project(projectId),
    queryFn: () => api.getProject(projectId),
  });
  const tasks = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });
  const team = useQuery({
    queryKey: queryKeys.team(),
    queryFn: () => api.listTeam(),
  });
  const me = useQuery({ queryKey: queryKeys.me(), queryFn: api.me });
  const [editOpen, setEditOpen] = useState(false);

  if (project.isLoading) {
    return (
      <div style={{ padding: 30 }}>
        <Skeleton count={6} height={120} />
      </div>
    );
  }
  if (project.isError) {
    return (
      <div style={{ padding: 30 }}>
        Couldn&rsquo;t load this project. Try again.
      </div>
    );
  }
  const proj = project.data?.project;
  if (!proj) return <div style={{ padding: 30 }}>Project not found.</div>;

  const allTasks = tasks.data?.tasks ?? [];
  const totalCount = allTasks.length;
  const completedTasksCount = allTasks.filter(
    (t) => t.status === "done",
  ).length;

  const ownerMember = team.data?.members?.find((m) => m.id === proj.ownerId);
  const ownerName = ownerMember?.name ?? "—";

  // parseDateLocal: date-only strings (which the edit modal now writes)
  // parse as UTC midnight via `new Date(...)` and display a day early for
  // users west of UTC.
  const targetDateParsed = parseDateLocal(proj.targetDate);
  const dueDate = targetDateParsed
    ? Math.floor(targetDateParsed.getTime() / 1000)
    : null;

  // PATCH /projects/:id is owner-gated server-side; only show the pencil
  // to the owner so members don't hit a 403 they can't act on.
  const isOwner = me.data?.user?.id != null && me.data.user.id === proj.ownerId;

  return (
    <div className="proj-shell">
      <div className="proj-shell-head">
        <div className="proj-head">
          <div className="crumbs" style={{ padding: "14px 32px 0" }}>
            <Link to="/projects">Projects</Link>
            <span style={{ margin: "0 6px", color: "var(--muted)" }}>/</span>
            <span style={{ color: "var(--ink)" }}>{proj.name}</span>
          </div>

          <div style={{ padding: "12px 32px 0" }}>
            <ProjectHeader
              project={{
                id: proj.id,
                name: proj.name,
                ownerName,
                ownerAvatarUrl: ownerMember?.image ?? null,
                dueDate,
                completedCount: completedTasksCount,
                totalCount,
                status: proj.status,
                tags: proj.tags,
              }}
              onEdit={isOwner ? () => setEditOpen(true) : undefined}
            />
            <ProjectEditModal
              project={proj}
              open={editOpen}
              onClose={() => setEditOpen(false)}
            />
          </div>

          <div className="tabs" style={{ padding: "0 32px" }}>
            <Link
              to="/projects/$projectId"
              params={{ projectId }}
              className={`tab${active === "board" ? " active" : ""}`}
            >
              <Icon name="board" size={14} /> Tasks{" "}
              <span className="tcount">{totalCount}</span>
            </Link>
            <Link
              to="/projects/$projectId/milestones"
              params={{ projectId }}
              className={`tab${active === "milestones" ? " active" : ""}`}
            >
              <Icon name="flag" size={14} /> Milestones
            </Link>
            <Link
              to="/projects/$projectId/epics"
              params={{ projectId }}
              className={`tab${active === "epics" ? " active" : ""}`}
            >
              <Icon name="grid" size={14} /> Epics
            </Link>
            <Link
              to="/projects/$projectId/docs"
              params={{ projectId }}
              className={`tab${active === "docs" ? " active" : ""}`}
            >
              <Icon name="clip" size={14} /> Docs
            </Link>
            <Link
              to="/projects/$projectId/wiki"
              params={{ projectId }}
              className={`tab${active === "wiki" ? " active" : ""}`}
            >
              <Icon name="folder" size={14} /> Wiki
            </Link>
            <Link
              to="/projects/$projectId/requirements"
              params={{ projectId }}
              className={`tab${active === "requirements" ? " active" : ""}`}
            >
              <Icon name="check" size={14} /> Requirements
            </Link>
            <Link
              to="/projects/$projectId/reports"
              params={{ projectId }}
              className={`tab${active === "reports" ? " active" : ""}`}
            >
              <Icon name="arrow-u" size={14} /> Reports
            </Link>
            <Link
              to="/projects/$projectId/activity"
              params={{ projectId }}
              className={`tab${active === "activity" ? " active" : ""}`}
            >
              <Icon name="clock" size={14} /> Activity
            </Link>
            <Link
              to="/projects/$projectId/members"
              params={{ projectId }}
              className={`tab${active === "members" ? " active" : ""}`}
            >
              <Icon name="users" size={14} /> Members
            </Link>
            <Link
              to="/projects/$projectId/files"
              params={{ projectId }}
              className={`tab${active === "files" ? " active" : ""}`}
            >
              <Icon name="folder" size={14} /> Files
            </Link>
            {/* Settings is owner-only: every action on it hits an owner-gated
                endpoint, so members would only meet 403s. */}
            {isOwner && (
              <Link
                to="/projects/$projectId/settings"
                params={{ projectId }}
                className={`tab${active === "settings" ? " active" : ""}`}
              >
                <Icon name="settings" size={14} /> Settings
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="proj-shell-body">
        <Outlet />
      </div>
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId")({
  component: ProjectLayout,
});
